// src/utils/errorReporting.ts
// Utility wrapper around Sentry's captureException for enriched error reporting.
// Use captureError() in catch blocks alongside existing showToast() calls to send
// errors to Sentry with optional contextual metadata (page, action, identifiers).
// Use setSentryUser() in AuthContext when the session changes.
// Use addBreadcrumb() to trace critical user actions before errors occur —
//   particularly for RLS-sensitive operations (form loading, document access, etc.).

import * as Sentry from '@sentry/react';

export function captureError(
  error: unknown,
  context?: Record<string, unknown>,
): void {
  if (!context) {
    Sentry.captureException(error);
    return;
  }

  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

export function setSentryUser(
  user: { id: string; email: string; isAdmin: boolean } | null,
): void {
  if (user) {
    Sentry.setUser({ id: user.id, email: user.email, isAdmin: user.isAdmin });
  } else {
    Sentry.setUser(null);
  }
}

/**
 * Records a breadcrumb in Sentry to trace the sequence of actions leading to an error.
 * Use this on critical data-access operations so that if an error occurs downstream,
 * the Sentry issue includes context about what was attempted just before.
 *
 * category conventions:
 *   'data.read'   — SELECT queries
 *   'data.write'  — INSERT / UPDATE / DELETE
 *   'auth'        — authentication / permission checks
 *   'navigation'  — page/route changes
 */
export function addBreadcrumb(
  message: string,
  category: 'data.read' | 'data.write' | 'auth' | 'navigation',
  data?: Record<string, unknown>,
  level: Sentry.SeverityLevel = 'info',
): void {
  Sentry.addBreadcrumb({
    message,
    category,
    level,
    data,
    timestamp: Date.now() / 1000,
  });
}
